import {stepPortalCrossing,resolvePortalPair,transformPortalPoint,portalToLocal,portalHeadingDelta} from './portal-math.mjs';

/** The native owner publishes a complete pose. Borrowed presentation flags are
 * never ownership, and copying a view must not replace the chosen lens. */
export function publishNativeCamera(camera,writePose) {
  if(camera.parent)throw TypeError('Native camera must be unparented');
  camera.matrixAutoUpdate=true;
  camera.matrixWorldAutoUpdate=true;
  const result=writePose?.();
  if(result?.then)throw TypeError('Synchronous camera publication required');
  camera.matrixAutoUpdate=true;
  camera.matrixWorldAutoUpdate=true;
  camera.updateMatrix();
  camera.updateMatrixWorld(true);
  return camera;
}
const at=(worlds,id)=>worlds instanceof Map?worlds.get(id):worlds?.[id];
const identity=player=>JSON.stringify([player?.id,player?.accountId??null]);
const stamp=(world,portal)=>JSON.stringify([world.id,world.revision??null,portal.id,portal.targetWorldId,portal.targetPortalId,portal.position,portal.yaw??0,portal.width??null,portal.height??null,portal.aperture??null]);
const copyPortal=portal=>Object.freeze({...portal,position:Object.freeze({...portal.position})});

/** A collision-resolved native crossing can precede the socket membership ACK.
 * This record moves only presentation into the physical body's chart. Controller
 * state, input, yaw and server membership remain untouched until native receipt. */
export function createPendingCameraTraversal() {
  let pending=null,arrival=null,observedCooldown=null;
  function clear(){pending=null;arrival=null;observedCooldown=null;}
  function valid(player,world,worlds,record=pending) {
    if(!record)return false;
    const pending=record;
    const source=world?.portals?.find(p=>p.id===pending.source.id);
    const pair=source&&resolvePortalPair(world,source,worlds);
    if(identity(player)!==pending.identity||JSON.stringify([player.crossingSeq??0,player.portalTraversal?.sequence??0])!==pending.authority||world?.id!==pending.sourceWorldId||player.worldId!==world.id||!pair||pair.world.id!==pending.targetWorldId||stamp(world,source)!==pending.sourceStamp||stamp(pair.world,pair.target)!==pending.targetStamp) {
      clear();return false;
    }
    return true;
  }
  return Object.freeze({
    clear,
    accept({player,sourceWorld,targetWorld}) {
      clear();
      const receipt=player.portalTraversal,source=sourceWorld?.portals?.find(portal=>portal.id===receipt?.sourcePortalId),target=targetWorld?.portals?.find(portal=>portal.id===receipt?.targetPortalId);
      if(!source||!target||receipt.sourceWorldId!==sourceWorld.id||receipt.targetWorldId!==targetWorld.id||portalToLocal(player.position,target).z*receipt.targetSide>=0)return;
      // An ACK can precede the local sweep, or arrive after a local reversal.
      // Membership is now authoritative in the destination while the physical
      // predicted body/eye still belong to the source chart. Retain that chart
      // until the body completes this already-accepted crossing.
      arrival=Object.freeze({identity:identity(player),sourceWorldId:targetWorld.id,targetWorldId:sourceWorld.id,source:copyPortal(target),target:copyPortal(source),sourceStamp:stamp(targetWorld,target),targetStamp:stamp(sourceWorld,source),authority:JSON.stringify([player.crossingSeq??0,player.portalTraversal?.sequence??0]),sourceSide:receipt.targetSide,targetSide:receipt.sourceSide,headingDelta:portalHeadingDelta(target,source,receipt.targetSide,receipt.sourceSide),traversal:Object.freeze({...receipt,sourceWorldId:targetWorld.id,targetWorldId:sourceWorld.id,sourcePortalId:target.id,targetPortalId:source.id,sourceSide:receipt.targetSide,targetSide:receipt.sourceSide})});
    },
    motion({player,world,worlds}) {
      if(arrival&&valid(player,world,worlds,arrival)) {
        return portalToLocal(player.position,arrival.source).z*arrival.sourceSide<0?arrival:null;
      }
      if(!valid(player,world,worlds))return null;
      if(portalToLocal(player.position,pending.source).z*pending.sourceSide>1e-7)return null;
      return pending;
    },
    observe({previous,player,world,worlds,dt,enabled=true,blockedPortalWorldIds=[]}={}) {
      if(!enabled){clear();return;}
      if(arrival) {
        if(!valid(player,world,worlds,arrival))return;
        if(portalToLocal(player.position,arrival.source).z*arrival.sourceSide>=0)arrival=null;
        return;
      }
      const cooldownKey=JSON.stringify([identity(player),player.worldId,player.lastPortalId??null,player.crossingSeq??0]);
      const rawCooldown=Number.isFinite(player.portalCooldownRemaining)?player.portalCooldownRemaining:0;
      if(!observedCooldown||observedCooldown.key!==cooldownKey||observedCooldown.raw!==rawCooldown)observedCooldown={key:cooldownKey,raw:rawCooldown,remaining:rawCooldown};
      const cooldown=observedCooldown.remaining;
      observedCooldown.remaining=Math.max(0,cooldown-Math.max(0,Number.isFinite(dt)?dt:0));
      if(pending) {
        if(!valid(player,world,worlds))return;
        // Reversing the physical sweep cancels this unacknowledged crossing.
        if(portalToLocal(player.position,pending.source).z*pending.sourceSide>1e-7)pending=null;
        return;
      }
      if(!previous||identity(previous)!==identity(player)||previous.worldId!==world?.id||player.worldId!==world.id)return;
      const result=stepPortalCrossing(previous,{...player,portalCooldownRemaining:cooldown},world,worlds,dt,{preparedOnly:true,blockedPortalWorldIds});
      if(!result.crossing)return;
      const source=world.portals.find(p=>p.id===result.crossing.sourcePortalId),pair=source&&resolvePortalPair(world,source,worlds);
      if(!pair)return;
      const receipt=result.player.portalTraversal;
      pending=Object.freeze({identity:identity(player),sourceWorldId:world.id,targetWorldId:pair.world.id,source:copyPortal(source),target:copyPortal(pair.target),sourceStamp:stamp(world,source),targetStamp:stamp(pair.world,pair.target),authority:JSON.stringify([player.crossingSeq??0,player.portalTraversal?.sequence??0]),sourceSide:receipt.sourceSide,targetSide:receipt.targetSide,headingDelta:result.crossing.headingDelta,traversal:Object.freeze({...receipt})});
    },
    frame({player,world,worlds,target,desired,activePortalId,traversal}) {
      const record=arrival??pending;
      if(!valid(player,world,worlds,record))return {world,target,desired,activePortalId,traversal,headingDelta:0,pending:false};
      const map=p=>transformPortalPoint(p,record.source,record.target,record.sourceSide,record.targetSide);
      return {world:at(worlds,record.targetWorldId),target:map(target),desired:map(desired),activePortalId:record.target.id,traversal:record.traversal,headingDelta:record.headingDelta,pending:true,crossing:record};
    },
    status:()=>pending,
  });
}
export function cameraRouteHeading(frame,route) {
  const crossing=route?.crossing;
  return frame.headingDelta+(crossing?portalHeadingDelta(crossing.source,crossing.target,crossing.sourceSide,crossing.targetSide):0);
}

/** Use the same provisional body chart for native straddling and its main draw.
 * Animation/controller ownership stays with the source root. Restore its exact
 * transform and parent after the synchronous presentation transaction. */
const bodyScratch=new WeakMap();
export function withPendingBodyPresentation({THREE:T,root,frame,scene},draw) {
  if(!frame.pending)return draw();
  if(!scene?.isScene)throw TypeError('Prepared destination scene required');
  const crossing=frame.crossing,parent=root.parent,index=parent?.children.indexOf(root);
  const priorNeeds=root.matrixWorldNeedsUpdate;
  root.updateWorldMatrix(true,false);scene.updateWorldMatrix(true,false);
  let saved=bodyScratch.get(root);if(!saved){saved={matrix:new T.Matrix4(),world:new T.Matrix4(),transform:new T.Matrix4(),mapped:new T.Matrix4(),active:false};bodyScratch.set(root,saved);}
  if(saved.active)throw Error('Nested body presentation is unsupported');
  saved.matrix.copy(root.matrix);saved.world.copy(root.matrixWorld);saved.auto=root.matrixAutoUpdate;saved.worldAuto=root.matrixWorldAutoUpdate;saved.needs=priorNeeds;saved.active=true;
  const origin=transformPortalPoint({x:0,y:0,z:0},crossing.source,crossing.target,crossing.sourceSide,crossing.targetSide);
  const transform=saved.transform.makeRotationY(frame.headingDelta).setPosition(origin.x,origin.y,origin.z);
  const mapped=saved.mapped.copy(scene.matrixWorld).invert().multiply(transform).multiply(saved.world);
  try {
    // Borrow only render matrices, exactly as the activity presentation path does.
    // Decomposing/restoring TRS canonicalizes Euler yaw and corrupts the next
    // controller-owned rotation.y update even when the quaternion is unchanged.
    scene.add(root);
    root.matrix.copy(mapped);root.matrixAutoUpdate=false;root.matrixWorldAutoUpdate=true;root.matrixWorldNeedsUpdate=true;root.updateMatrixWorld(true);
    const result=draw();if(result?.then)throw TypeError('Synchronous body presentation required');return result;
  } finally {
    root.removeFromParent();
    if(parent){parent.add(root);parent.children.splice(parent.children.indexOf(root),1);parent.children.splice(Math.min(index,parent.children.length),0,root);}
    root.matrix.copy(saved.matrix);
    root.matrixAutoUpdate=saved.auto;root.matrixWorld.copy(saved.world);root.matrixWorldAutoUpdate=saved.worldAuto;root.matrixWorldNeedsUpdate=saved.needs;
    for(const child of root.children)child.updateMatrixWorld(true);
    root.traverse(object=>object.skeleton?.update());saved.active=false;
  }
}
